import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DisruptionScope } from "@/components/disruption/disruptionMetrics";
import { components } from "@/lib/openapi";
import { getFlightData } from "@/queries/flights";
import { useMemo } from "react";

type Flight = components["schemas"]["FlightSchema"];

export type DisruptionModalFilter =
  | "all_canceled"
  | "canceled_arrivals"
  | "canceled_departures"
  | "impacted_total"
  | "impacted_arrivals"
  | "impacted_departures";

interface CanceledFlightsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reportId: number;
  filter: DisruptionModalFilter | null;
  title: string | null;
  scope?: DisruptionScope;
}

function isCanceled(flight: Flight): boolean {
  return (flight.status_oag || "").toLowerCase().includes("cancel");
}

function matchesScope(flight: Flight, scope?: DisruptionScope): boolean {
  if (!scope) {
    return true;
  }

  const gateNames = scope.gateNames?.length ? new Set(scope.gateNames) : null;
  const terminalNames = scope.terminalNames?.length
    ? new Set(scope.terminalNames)
    : null;

  if (!gateNames && !terminalNames) {
    return true;
  }

  const gateMatch = gateNames ? gateNames.has(flight.gate || "") : true;
  const terminalMatch = terminalNames
    ? terminalNames.has(flight.terminal || "")
    : true;

  return gateMatch && terminalMatch;
}

const TITLE_MAP: Record<DisruptionModalFilter, string> = {
  all_canceled: "Canceled Flights",
  canceled_arrivals: "Canceled Arrivals",
  canceled_departures: "Canceled Departures",
  impacted_total: "Passenger Demand Removed",
  impacted_arrivals: "Arrival Passenger Demand Removed",
  impacted_departures: "Departure Passenger Demand Removed",
};

export function CanceledFlightsModal({
  open,
  onOpenChange,
  reportId,
  filter,
  title,
  scope,
}: CanceledFlightsModalProps) {
  const { data: arrivals, isLoading: arrivalsLoading } = getFlightData(
    reportId,
    "arrival"
  );
  const { data: departures, isLoading: departuresLoading } = getFlightData(
    reportId,
    "departure"
  );

  const isLoading = arrivalsLoading || departuresLoading;

  const flights = useMemo(() => {
    const all = [...(arrivals || []), ...(departures || [])]
      .filter(isCanceled)
      .filter((flight) => matchesScope(flight, scope));

    if (!filter) return all;

    switch (filter) {
      case "canceled_arrivals":
      case "impacted_arrivals":
        return all.filter((f) => f.direction === "arrival");
      case "canceled_departures":
      case "impacted_departures":
        return all.filter((f) => f.direction === "departure");
      default:
        return all;
    }
  }, [arrivals, departures, filter, scope]);

  const showPassengerEstimate =
    filter === "impacted_total" ||
    filter === "impacted_arrivals" ||
    filter === "impacted_departures";

  const dialogTitle =
    title || (filter ? TITLE_MAP[filter] : "Canceled Flights");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[calc(100vh-250px)] max-h-[500px]">
          {isLoading && <div className="text-slate-400">Loading...</div>}
          {!isLoading && flights.length === 0 && (
            <div className="text-slate-400">No canceled flights found.</div>
          )}
          {!isLoading && flights.length > 0 && (
            <div>
              {flights.map((flight) => (
                <div
                  key={flight.id}
                  className="flex justify-between items-start py-3 border-t border-slate-700/50"
                >
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-base">
                        {flight.number}
                      </span>
                      <Badge className="bg-red-500/20 text-red-300 border border-red-500/30">
                        Canceled
                      </Badge>
                      <Badge variant="secondary">
                        {flight.direction === "arrival" ? "Arrival" : "Departure"}
                      </Badge>
                    </div>
                    <div className="text-sm text-slate-400">
                      {flight.dep_iata} → {flight.arr_iata}
                      {flight.gate && (
                        <span className="ml-2">
                          • Gate{" "}
                          <span className="font-medium">{flight.gate}</span>
                        </span>
                      )}
                      {(flight.direction === "arrival"
                        ? flight.sch_arr_time
                        : flight.sch_dep_time) && (
                        <span className="ml-2">
                          •{" "}
                          {flight.direction === "arrival"
                            ? flight.sch_arr_time
                            : flight.sch_dep_time}
                        </span>
                      )}
                    </div>
                  </div>
                  {showPassengerEstimate && flight.estimated_passengers != null && (
                    <span className="font-medium text-base">
                      {flight.estimated_passengers.toLocaleString()} pax
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
